import { useReduxState } from 'hooks';

import FilterButtonItems from './FilterButtonItems';
import FilterButtonOpenInput from './FilterButtonOpenInput';
import FilterButtonRoot from './FilterButtonRoot';
import { useFiltersContext, useFiltersDispatch } from './FilterContext';

import type { FilterItem } from '.';

interface Props {
  filter: FilterItem;
  filterId: string;
  onClear: () => void;
  triggerClause: string;
  triggerSubClause: string;
  triggerSubTitle: string;
  triggerTarget: string;
  triggerTitle: string;
}

const FiltersActiveDropdown = ({
  filter,
  filterId,
  onClear,
  triggerClause,
  triggerSubClause,
  triggerSubTitle,
  triggerTarget,
  triggerTitle
}: Props) => {
  const { theme } = useReduxState();

  const { state } = useFiltersContext();

  const filterDispatch = useFiltersDispatch();

  const handleInputChange = (
    value: { [value: string]: true },
    field: string
  ) => {
    if (!value && !state?.[filterId]?.[field]) return;

    if (!value && state?.[filterId]?.[field]) {
      let newState = { ...state?.[filterId] };
      delete newState[field];
      filterDispatch({ type: 'updateState', state: newState, filterId });
    } else {
      filterDispatch({
        type: 'updateState',
        filterId,
        state: { ...state?.[filterId], [field]: value }
      });
    }
  };

  return (
    <div>
      <FilterButtonRoot
        contentSideOffset={-10}
        contentTopOffset={10}
        numberOfActiveFilters={0}
        onClear={onClear}
        onSubmit={() => filterDispatch({ type: 'submitFilters', filterId })}
        portalId={`filters-active-portal-${filterId}`}
        trigger={
          <div className="trigger">
            <span>{triggerTarget}</span>
            <span style={{ fontWeight: 400 }}> {triggerClause} </span>
            <span>{triggerTitle}</span>
            {triggerSubClause && (
              <span style={{ fontWeight: 400 }}> {triggerSubClause} </span>
            )}
            {triggerSubTitle && <span>{triggerSubTitle}</span>}
          </div>
        }
      >
        {({ setRootIsOpen }) =>
          filter.items?.length ? (
            <FilterButtonItems
              filterId={filterId}
              parentPortalId={`filters-active-portal-${filterId}`}
              selectAllOnClick={filter.selectAllOnClick}
              setRootIsOpen={setRootIsOpen}
              {...filter}
            />
          ) : filter.input ? (
            <FilterButtonOpenInput
              {...filter}
              filterId={filterId}
              initialValue={
                Object.keys(state?.[filterId]?.[filter.field] || {})[0]
              }
              onInputChange={(value: string) =>
                handleInputChange(
                  value ? { [value]: true } : null,
                  filter.field
                )
              }
              setRootIsOpen={setRootIsOpen}
            />
          ) : null
        }
      </FilterButtonRoot>

      <style jsx>{`
        .trigger {
          cursor: pointer;
        }
        .active-filter:not(:first-child),
        .sub-item:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .sub-item:first-child {
          padding-top: 1px;
        }
        .input {
          padding: 5px 10px;
        }
      `}</style>
    </div>
  );
};

export default FiltersActiveDropdown;
